import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface LowCapToken {
  id: string;
  token_symbol: string;
  token_name: string;
  token_id: string;
  collection_date: string;
  current_price: number;
  market_cap: number;
  volume_24h: number | null;
  price_change_24h: number | null;
  price_change_7d: number | null;
  opportunity_score: number;
  risk_score: number | null;
  momentum_score: number | null;
  volume_surge_score: number | null;
  social_score: number | null;
  buy_signals: Array<{ type: string; strength: string; description: string }>;
  narrative: string | null;
  ai_analysis: string | null;
  collected_at: string;
}

export interface MarketAnalysis {
  id: string;
  analysis_date: string;
  sentiment_analysis: string;
  technical_outlook: string;
  catalyst_watch: string;
  risk_warning: string;
  trending_narratives: string[] | null;
  market_conditions: Record<string, unknown> | null;
  created_at: string;
}

export const useLowCapOpportunities = (limit = 5) => {
  return useQuery({
    queryKey: ['low-cap-opportunities', limit],
    queryFn: async () => {
      // Find the latest collection_date so we never show stale historical picks
      const { data: latestRow, error: latestErr } = await supabase
        .from('low_cap_opportunities')
        .select('collection_date')
        .order('collection_date', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (latestErr) throw latestErr;
      if (!latestRow?.collection_date) return [] as LowCapToken[];

      const { data, error } = await supabase
        .from('low_cap_opportunities')
        .select('*')
        .eq('collection_date', latestRow.collection_date)
        .order('opportunity_score', { ascending: false })
        .limit(limit);

      if (error) throw error;

      return (data || []).map(d => ({
        ...d,
        buy_signals: (d.buy_signals || []) as Array<{ type: string; strength: string; description: string }>,
      })) as LowCapToken[];
    },
    staleTime: 5 * 60 * 1000,
    refetchInterval: 10 * 60 * 1000,
  });
};

export const useLowCapMarketAnalysis = () => {
  return useQuery({
    queryKey: ['low-cap-market-analysis'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('low_cap_market_analysis')
        .select('*')
        .order('analysis_date', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data as MarketAnalysis | null;
    },
    staleTime: 5 * 60 * 1000,
    refetchInterval: 10 * 60 * 1000,
  });
};

export const useRunLowCapAnalysis = () => {
  const runAnalysis = async () => {
    const { data, error } = await supabase.functions.invoke('low-cap-analysis', {
      body: { include_ai: true },
    });
    if (error) throw error;
    return data;
  };

  return { runAnalysis };
};
